<script>
  // One column, one question at a time. A column is described by three separate
  // decisions — what it is, how it may be published, and how its values are
  // rewritten — and most visits only concern one of them. The editor opens on the
  // facet that needs attention and shows only that, with the preview always
  // present because it is the feedback for all three.
  import Alert from './Alert.svelte';
  import Button from './Button.svelte';
  import TransformChain from './TransformChain.svelte';
  import {
    IDENTIFIES_LABELS, SENSITIVITY_LABELS, TREATMENT_LABELS,
    applyCatalogTerm, applyManualTerm, retermKeepingLabels, isValidTermId,
    setPosture, setTreatment, resetPosture, validateTransform,
    overridesFor, findTerm, termPosture, ensureRecord, sampleRowsForColumn, traceColumn,
  } from '../../../lib/disclosure_core.js';
  import { catalogTermMatches } from '../../../lib/mapping_workbench.js';
  import { termSchemaLabel } from '../../../lib/schema_label.js';

  let { column, catalog, mapping, datasets = [], rows = [], headers = [], profile = null, facet = null,
        onchange = () => {}, ondrop = () => {}, onclose = () => {} } = $props();

  const term = $derived(findTerm(catalog, column.term));
  const suggested = $derived(termPosture(term));
  const overrides = $derived(overridesFor(column, catalog));
  const transformErrors = $derived(validateTransform(column.transform));
  const recordLabel = (id) => id ?? 'no record yet';

  // Unfinished business decides where an unspecified visit lands, so a column
  // restored from the dropped list opens on the term it still needs.
  const needs = $derived({ term: !column.term, labels: false, transform: transformErrors.length > 0 });
  const FACETS = [
    { id: 'term', label: 'What it is' },
    { id: 'labels', label: 'Labels' },
    { id: 'transform', label: 'Transform' },
  ];
  // Seeded once: the parent keys this component on column + facet, so a new visit
  // remounts with a fresh starting point rather than inheriting the last one.
  // svelte-ignore state_referenced_locally
  let active = $state(facet ?? (!column.term ? 'term' : 'transform'));
  // svelte-ignore state_referenced_locally
  let picking = $state(!column.term);
  let query = $state('');
  let manual = $state(false);
  let manualId = $state('');
  let manualRecord = $state('');
  let addedRecord = $state(null);
  let pendingTerm = $state(null);

  // The terms of the datasets this source fills come first, each named by its
  // dataset, so a file that fills several reads as which part feeds which.
  const filled = $derived(Object.keys(mapping?.records ?? {}));
  const recordsOf = (term) => [term.record].flat().filter(Boolean);
  const datasetOf = (term) => datasets.find((d) => recordsOf(term).some((r) => d.recordClasses.includes(r)))?.label ?? null;
  const matches = $derived.by(() => {
    const needle = query.trim().toLowerCase();
    const terms = (catalog?.terms ?? []).filter((term) => !needle || catalogTermMatches(catalog, term, needle));
    const ours = (term) => recordsOf(term).some((r) => filled.includes(r));
    return [...terms.filter(ours), ...terms.filter((term) => !ours(term))];
  });

  // Distinct values say far more about a transform than the first few rows do.
  const trace = $derived.by(() =>
    sampleRowsForColumn(rows, column.source, 6).map((row) => traceColumn(column, row)));

  // Replacing a term would normally take the new term's posture with it. When the
  // analyst has deliberately overridden the old one, that is a judgement worth
  // asking about rather than discarding, so the choice is put to them.
  function choose(next) {
    if (overrides.posture || overrides.treatment) { pendingTerm = next; return; }
    commitTerm(next, false);
  }

  function commitTerm(next, keepLabels) {
    addedRecord = ensureRecord(mapping, next.record) ? next.record : null;
    if (keepLabels) retermKeepingLabels(column, next, catalog);
    else applyCatalogTerm(column, next);
    pendingTerm = null; picking = false; manual = false; query = '';
    onchange();
  }

  const manualError = $derived(
    !manualId.trim() ? null
      : isValidTermId(manualId) ? null
      : 'Use a compact term like ops:greenRoomRider, or a full http(s) address.');

  function useManual() {
    if (!isValidTermId(manualId) || !manualRecord) return;
    applyManualTerm(column, manualId.trim(), manualRecord);
    picking = false; manual = false;
    onchange();
  }

  const changePosture = (patch) => { setPosture(column, patch, catalog); onchange(); };
  const changeTreatment = (value) => { setTreatment(column, value, catalog); onchange(); };
  const reset = () => { resetPosture(column, catalog); onchange(); };
</script>

<p class="wf2-caption">
  {#if profile}
    {profile.nonEmpty} values · {profile.distinct} distinct · {profile.blank} blank
  {:else}
    Not present in the imported file
  {/if}
</p>

<div class="wf2-tabs" role="tablist" aria-label="What to edit">
  {#each FACETS as item}
    <button
      class="wf2-tab {active === item.id ? 'active' : ''}"
      role="tab"
      aria-selected={active === item.id}
      onclick={() => (active = item.id)}
    >{item.label}{#if needs[item.id]}<span class="needs-dot" aria-label="needs attention">•</span>{/if}</button>
  {/each}
</div>

<!-- A one-line reminder of the two facets not on screen, so narrowing the view
     never hides what the column currently claims. -->
<p class="facet-context">
  {#if active !== 'term'}<span class="mono">{column.term || 'no term yet'}</span> · {/if}
  {#if active !== 'labels'}{column.identifies} · {column.sensitivity} → {column.treatment}{/if}
</p>

{#if active === 'term'}
  <section class="drawer-section">
    <h3 class="drawer-heading">What this column is</h3>

    {#if column.term}
      <div class="term-choice">
        <div class="mono">{column.term}</div>
        {#if term}
          <p class="wf2-caption">{term.means}</p>
          <p class="wf2-caption">From {termSchemaLabel(catalog, term.id) ?? term.from}</p>
          {#if term.note}<p class="wf2-caption">{term.note}</p>{/if}
        {:else}
          <p class="wf2-caption">Entered by hand — not a catalog term.</p>
        {/if}
        <p class="wf2-caption">Becomes part of the <span class="mono">{recordLabel(column.record)}</span> record.</p>
      </div>
    {:else}
      <Alert title="No term chosen yet" variant="warning">
        <p>This column needs a shared term before it can be published. The term also decides which record it joins.</p>
      </Alert>
    {/if}

    {#if column.term}
      <div class="wf2-row" style="gap:12px; margin-top:12px">
        <Button variant="secondary" onclick={() => { picking = !picking; manual = false; }}>
          {picking ? 'Cancel' : 'Change term'}
        </Button>
      </div>
    {/if}

    {#if addedRecord}
      <Alert title="A record class was added" variant="info">
        <p>
          This term belongs to <span class="mono">{addedRecord}</span>, which this mapping does not have yet.
          It has been added; its records are stored with the rest of its dataset's.
        </p>
      </Alert>
    {/if}

    {#if pendingTerm}
      <Alert title="You have changed this column's labels" variant="warning">
        <p>
          Switching to <span class="mono">{pendingTerm.id}</span> can take that term's suggested labels
          ({IDENTIFIES_LABELS[pendingTerm.posture.identifies]} · {SENSITIVITY_LABELS[pendingTerm.posture.sensitivity]}
          → {termPosture(pendingTerm).treatment}), or keep the labels you set
          ({IDENTIFIES_LABELS[column.identifies]} · {SENSITIVITY_LABELS[column.sensitivity]} → {column.treatment}).
        </p>
        <div class="wf2-row" style="gap:12px">
          <Button variant="primary" onclick={() => commitTerm(pendingTerm, true)}>Keep my labels</Button>
          <Button variant="secondary" onclick={() => commitTerm(pendingTerm, false)}>Use the catalog's labels</Button>
          <Button variant="ghost" onclick={() => (pendingTerm = null)}>Cancel</Button>
        </div>
      </Alert>
    {/if}

    {#if picking}
      {#if manual}
        <div style="margin-top:12px">
          <label class="wf2-field">Term
            <input class="wf2-control mono" bind:value={manualId} placeholder="ops:greenRoomRider"
                   aria-invalid={Boolean(manualError)} aria-describedby="manual-term-error" />
          </label>
          <p class="wf2-caption" id="manual-term-error" style={manualError ? 'color:var(--danger)' : ''}>
            {manualError ?? 'A compact term like ops:greenRoomRider, or a full http(s) address.'}
          </p>
          <label class="wf2-field">Belongs to the record
            <select class="wf2-control" bind:value={manualRecord}>
              <option value="">Choose…</option>
              {#each Object.keys(mapping.records) as id}<option value={id}>{id}</option>{/each}
            </select>
          </label>
          <p class="wf2-caption">
            A hand-entered term carries no catalog posture, so you set both labels yourself.
            Proposing it to the shared catalog is a separate, later act.
          </p>
          <div class="wf2-row" style="gap:12px">
            <Button variant="primary" disabled={!isValidTermId(manualId) || !manualRecord} onclick={useManual}>Use this term</Button>
            <Button variant="ghost" onclick={() => (manual = false)}>Back to the catalog</Button>
          </div>
        </div>
      {:else}
        <div style="margin-top:12px">
          <label class="wf2-field">Search the shared catalog
            <input class="wf2-control" type="search" bind:value={query} placeholder="gender, duration, region…" />
          </label>
          <ul class="term-results">
            {#each matches as match}
              <li>
                <button class="term-result" aria-current={match.id === column.term} onclick={() => choose(match)}>
                  <div class="mono">{match.id}</div>
                  <div class="wf2-caption">{match.means}</div>
                  <div class="wf2-caption">From {termSchemaLabel(catalog, match.id) ?? match.from}</div>
                  <div class="wf2-caption">
                    {#if datasetOf(match)}{datasetOf(match)} · {/if}{match.record} · {match.posture.identifies} · {match.posture.sensitivity}
                    → {termPosture(match).treatment}
                  </div>
                </button>
              </li>
            {:else}
              <li class="wf2-caption">No catalog term matches “{query}”.</li>
            {/each}
          </ul>
          <Button variant="ghost" onclick={() => { manual = true; manualRecord = column.record ?? ''; manualId = column.term ?? ''; }}>
            Not in the catalog — enter a term by hand
          </Button>
        </div>
      {/if}
    {/if}
  </section>

{:else if active === 'labels'}
  <section class="drawer-section">
    <h3 class="drawer-heading">
      <span>How it may be published</span>
      {#if overrides.posture}<span class="tag">changed</span>
      {:else if overrides.treatment}<span class="tag">treatment forced</span>{/if}
    </h3>

    <p class="wf2-text">
      {#if suggested && !overrides.posture && !overrides.treatment}From the catalog: identifies{:else}Identifies{/if}
      a person {IDENTIFIES_LABELS[column.identifies]}, {SENSITIVITY_LABELS[column.sensitivity]}
      — so it is <strong>{TREATMENT_LABELS[column.treatment]}</strong>.
    </p>
    {#if suggested && overrides.posture}
      <p class="wf2-caption">
        The catalog suggested {IDENTIFIES_LABELS[suggested.identifies]} · {SENSITIVITY_LABELS[suggested.sensitivity]}
        → {suggested.treatment}. Your change is recorded in the mapping.
      </p>
    {/if}
    {#if overrides.treatment}
      <p class="wf2-caption">These labels derive <span class="mono">{overrides.suggested}</span>; you set <span class="mono">{column.treatment}</span> instead.</p>
    {/if}

    <label class="wf2-field">Does this identify a person?
      <select class="wf2-control" value={column.identifies} onchange={(e) => changePosture({ identifies: e.currentTarget.value })}>
        {#each Object.entries(IDENTIFIES_LABELS) as [value, label]}<option {value}>{label}</option>{/each}
      </select>
    </label>
    <label class="wf2-field">How sensitive is it?
      <select class="wf2-control" value={column.sensitivity} onchange={(e) => changePosture({ sensitivity: e.currentTarget.value })}>
        {#each Object.entries(SENSITIVITY_LABELS) as [value, label]}<option {value}>{label}</option>{/each}
      </select>
    </label>
    <label class="wf2-field">Treatment
      <select class="wf2-control" value={column.treatment} onchange={(e) => changeTreatment(e.currentTarget.value)}>
        {#each Object.entries(TREATMENT_LABELS) as [value, label]}
          <option {value}>{value} — {label}{value === overrides.suggested ? ' (follows the labels)' : ''}</option>
        {/each}
      </select>
    </label>
    <p class="wf2-caption">
      Treatment normally follows the two labels. Changing it on its own is recorded separately,
      so a reviewer can tell a re-labelled column from an overruled one.
    </p>
    {#if term && (overrides.posture || overrides.treatment)}
      <Button variant="ghost" onclick={reset}>Reset to the catalog</Button>
    {/if}
  </section>

{:else}
  <section class="drawer-section">
    <h3 class="drawer-heading">How its values are rewritten</h3>
    <TransformChain {column} {catalog} {rows} {headers} {onchange} />
  </section>
{/if}

<section class="drawer-section">
  <h3 class="drawer-heading">
    <span>Preview</span>
    <span class="wf2-caption">{profile ? `${profile.distinct} distinct values in your file` : ''}</span>
  </h3>
  {#if !rows.length}
    <p class="wf2-caption">Load a CSV on the mapping screen to preview real values.</p>
  {:else}
    <div class="trace">
      {#each trace as row}
        <div class="trace-row">
          <span class="mono">{row.source || '(blank)'}</span>
          {#each row.steps as step}
            <span class="trace-hop" aria-hidden="true">→</span>
            <span class="mono">{step.values.length ? step.values.join(' · ') : '—'}</span>
          {/each}
          {#if row.error}
            <span class="trace-error">{row.error}</span>
          {:else if !row.values.length}
            <span class="trace-none">not published</span>
          {/if}
        </div>
      {/each}
    </div>
    <p class="wf2-caption">
      Values reaching “—” are dropped by that step. Exact values never leave the raw record;
      only what is shown on the right is written to the {recordLabel(column.record)} record.
    </p>
  {/if}
</section>

<section class="drawer-section">
  <div class="wf2-row" style="gap:12px">
    <Button variant="primary" onclick={onclose}>Done</Button>
    <Button variant="ghost" onclick={ondrop}>Don't publish this column</Button>
  </div>
  <p class="wf2-caption">
    Everything here is already part of your working design — there is nothing to save on this column.
    The whole design is written to your Pod at the last step, Confirm.
  </p>
</section>
